import React from 'react';
import { Minus, Plus } from 'lucide-react';
import { remainingFor, overPlanBy, photoBlockPacking, formatBoxes, showsStock } from './productionUtils';
import DueDate from '../common/DueDate';
import Meter from '../common/Meter';

// Row-level pieces shared by the table (desktop) and card (tablet/mobile) views.

// Number of distinct customers in a SKU group
export const groupCustomerCount = (wos) =>
  new Set(wos.map(w => w.customerName || w.customerCode).filter(Boolean)).size;

// Clicking anywhere on a row toggles it, except on its own controls.
export const isInteractiveTarget = (target) => Boolean(target.closest('input, select, button, a, label'));

export { DueDate };

// Reported vs planned for one work order, as numbers and a bar.
export const WoProgress = ({ reported, planned, remaining }) => (
  <div className="wo-progress" title={`${reported} of ${planned} reported, ${remaining} left`}>
    <div className="wo-progress-numbers">
      <span className="wo-progress-done">{reported}</span>
      <span className="wo-progress-planned">/ {planned}</span>
    </div>
    <Meter value={reported} max={planned} />
    <span className="wo-progress-left">{remaining === 0 ? 'Complete' : `${remaining} left`}</span>
  </div>
);

// Boxes and MDF sheets for what's still to make on a Photo Blocks work order.
export const BlockPacking = ({ wo }) => {
  const packing = photoBlockPacking(wo, remainingFor(wo));
  if (!packing) return null;
  const { perBox, fullBoxes, loose, sheets } = packing;
  const boxes = formatBoxes(fullBoxes, loose);
  const sheetText = `${Number(sheets.toFixed(2))} MDF ${sheets === 1 ? 'sheet' : 'sheets'}`;
  return (
    <span className="block-packing" title={`${perBox} per box`}>
      {boxes} · {sheetText}
    </span>
  );
};

// Priority's current stock of a frame / mount part (LOGPART BALANCE, Goods status), so the
// shelf can be counted against it. Nothing is shown until it has loaded.
export const StockBalance = ({ wo, balances, fetchedAt }) => {
  if (!showsStock(wo) || !balances) return null;
  const stock = balances[wo.partName];
  if (!stock) return null;
  const { available, locations, openOrders, projected } = stock;
  const tone = available < 0 ? 'negative' : available === 0 ? 'none' : 'ok';
  const where = locations.length > 0
    ? locations.map(l => `${l.warehouse} / ${l.location}${l.status === 'Goods' ? '' : ` (${l.status})`}: ${l.balance}`).join('\n')
    : 'No stock rows in Priority';
  const demand = openOrders > 0 ? `\nOpen orders: ${openOrders}\nProjected: ${projected}` : '';
  const asOf = fetchedAt ? `, as of ${fetchedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : '';
  return (
    <span
      className={`stock-balance stock-balance--${tone}`}
      title={`Priority stock${asOf}\n${where}${demand}\n\nIf the shelf doesn't match, earlier production was counted wrong.`}
    >
      Priority stock: {available}
      {locations.length === 1 && locations[0].location !== '0' && <> · {locations[0].location}</>}
      {locations.length > 1 && <> · {locations.length} locations</>}
      {openOrders > 0 && <> · {openOrders} on order, {projected} projected</>}
    </span>
  );
};

export const ShortBadge = () => (
  <span className="short-badge" title="Priority flags this work order as short of components. Raw materials may fail to issue when you report it.">
    Short
  </span>
);

// Reason selector for rejects. Only rendered once a reject quantity has been entered,
// and only when Priority actually returned active codes - a reject with no reason is a
// valid line, so this must never become a blocker.
export const DefectCodeSelect = ({ serialName, defectCodes, rejectQuantity, value, onChange }) => {
  if (defectCodes.length === 0 || !(rejectQuantity > 0)) return null;
  return (
    <select
      className="defect-code-select"
      value={value || ''}
      onChange={(e) => onChange(serialName, e.target.value)}
      onClick={(e) => e.stopPropagation()}
      aria-label={`Reject reason for ${serialName}`}
    >
      <option value="">Reject reason (optional)</option>
      {defectCodes.map(dc => (
        <option key={dc.code} value={dc.code}>
          {dc.code}{dc.description ? ` — ${dc.description}` : ''}
        </option>
      ))}
    </select>
  );
};

export const RejectQuantityInput = ({ serialName, value, onChange }) => (
  <input
    type="number"
    className={`reject-qty-input ${value > 0 ? 'has-rejects' : ''}`}
    value={value ?? ''}
    onChange={(e) => onChange(serialName, e.target.value)}
    min="0"
    step="1"
    inputMode="numeric"
    placeholder="0"
    aria-label={`Rejects for ${serialName}`}
  />
);

export const ReportQuantityInput = ({ wo, value, onChange }) => (
  <input
    type="number"
    className={`report-qty-input ${overPlanBy(wo, value) > 0 ? 'is-over' : ''}`}
    value={value ?? ''}
    onChange={(e) => onChange(wo.serialName, e.target.value)}
    min="0"
    step="1"
    inputMode="numeric"
    aria-label={`Quantity to report for ${wo.serialName}`}
  />
);

// To Report input with large -/+ buttons, for touch screens
export const ReportQuantityStepper = ({ wo, value, onChange }) => {
  const current = Number(value) || 0;
  return (
    <div className="qty-stepper">
      <button
        type="button"
        className="qty-stepper-btn"
        onClick={() => onChange(wo.serialName, String(Math.max(0, current - 1)))}
        disabled={current <= 0}
        aria-label={`One less for ${wo.serialName}`}
      >
        <Minus size={18} />
      </button>
      <ReportQuantityInput wo={wo} value={value} onChange={onChange} />
      <button
        type="button"
        className="qty-stepper-btn"
        onClick={() => onChange(wo.serialName, String(current + 1))}
        aria-label={`One more for ${wo.serialName}`}
      >
        <Plus size={18} />
      </button>
    </div>
  );
};

// Under the To Report input when it no longer matches what's left on the work order:
// an amber "Over by N" badge, or a "N left" link. Either one resets it to what's left.
export const ToReportHint = ({ wo, value, onQuantityChange }) => {
  const remaining = remainingFor(wo);
  if (value === '' || value === undefined || Number(value) === remaining) return null;
  const over = overPlanBy(wo, value);
  return (
    <button
      type="button"
      className={`to-report-hint ${over > 0 ? 'over' : ''}`}
      onClick={() => onQuantityChange(wo.serialName, String(remaining))}
      title={`Set to the ${remaining} left on this work order`}
    >
      {over > 0 ? `Over by ${over}` : `${remaining} left`}
    </button>
  );
};
